import {
  InsiderSlimBanner,
  InsiderSlimBannerProps,
} from "@faire/retailer-visitor-shared/components/InsiderSlimBanner/InsiderSlimBanner";
import { LazyInsiderEnrollmentModal } from "@faire/retailer-visitor-shared/components/Modals/InsiderModals/InsiderEnrollmentModal/LazyInsiderEnrollmentModal";
import { useInsiderAdvertisedBenefits } from "@faire/retailer-visitor-shared/hooks/insider/useInsiderAdvertisedBenefits";
import { useInsiderMembershipInformation } from "@faire/retailer-visitor-shared/hooks/insider/useInsiderMembershipInformation";
import { useInsiderState } from "@faire/retailer-visitor-shared/hooks/insider/useInsiderState";
import { useShowEligibleInsiderMessaging } from "@faire/retailer-visitor-shared/hooks/insider/useShowEligibleInsiderMessaging";
import { useMilestone } from "@faire/retailer-visitor-shared/stores/domain/useMilestone";
import { useUserStore } from "@faire/retailer-visitor-shared/stores/domain/useUserStore";
import { LoadingSpinner } from "@faire/web--source/slate/LoadingSpinner";
import { Spacer } from "@faire/web--source/slate/spacing";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { trackRetailerTeamManagementInsiderMarketingPageLearnMoreRedirectClick } from "@faire/web-api--source/events/retailerTeamManagement/click/insiderMarketingPageLearnMoreRedirect";
import { SEARCH_FILTER_PILL_INSIDER_BANNER_EUROPE } from "@faire/web-api--source/indigofair/data/IUserMilestone/RetailerMilestone/SEARCH_FILTER_PILL_INSIDER_BANNER_EUROPE";
import insiderMarketingPage from "@faire/web-api--source/routes/www/user/insider/info";
import React, { Suspense } from "react";
import { useHistory } from "react-router-dom";

export const InsiderSlimBannerContainer: React.FC = () => {
  const [isInsiderEnrolmentModalVisible, setIsInsiderEnrolmentModalVisible] =
    React.useState(false);

  const { setHasOccurred: setInsiderEuropeSearchBannerDismissed } =
    useMilestone(SEARCH_FILTER_PILL_INSIDER_BANNER_EUROPE);

  const advertisedBenefits = useInsiderAdvertisedBenefits();
  const { isInsiderPaused, isInsiderCancelled } = useInsiderState();
  const { isInsiderMembershipInformationFetching } =
    useInsiderMembershipInformation();

  const { push } = useHistory();

  const openInsiderEnrolmentModal = () => {
    setIsInsiderEnrolmentModalVisible(true);
  };

  const closeInsiderEnrolmentModal = () => {
    setIsInsiderEnrolmentModalVisible(false);
  };

  const goToInsiderMarketingPage = () => {
    push(insiderMarketingPage());
  };

  const { userCanPlaceOrder } = useUserStore(["userCanPlaceOrder"]);

  const action = () => {
    if (!userCanPlaceOrder) {
      trackRetailerTeamManagementInsiderMarketingPageLearnMoreRedirectClick(
        "slim-banner"
      );
      goToInsiderMarketingPage();
    } else {
      openInsiderEnrolmentModal();
    }
  };

  const membershipState: InsiderSlimBannerProps["membershipState"] =
    getMembershipState(isInsiderPaused, isInsiderCancelled);

  if (isInsiderMembershipInformationFetching) {
    return <LoadingSpinner size="small" />;
  }

  return (
    <>
      <InsiderSlimBanner
        benefits={advertisedBenefits}
        type={isInsiderPaused ? "PAUSED" : "UNENROLLED"}
        membershipState={membershipState}
        onAction={action}
        onClose={setInsiderEuropeSearchBannerDismissed}
      />
      <Spacer height="2x" />

      {/* Kim (chanyee.kim@faire.com): FD-85183 Render Insider enrolment modal as part of the component instead of using the query param driven app modal to avoid unnecessary request being triggered on the category page. */}
      {isInsiderEnrolmentModalVisible ? (
        <Suspense fallback={null}>
          <LazyInsiderEnrollmentModal onClose={closeInsiderEnrolmentModal} />
        </Suspense>
      ) : null}
    </>
  );
};

export const useCanRender = () => {
  const { hasOccurred: insiderEuropeSearchBannerDismissed } = useMilestone(
    SEARCH_FILTER_PILL_INSIDER_BANNER_EUROPE
  );
  const { isInsiderPaused } = useInsiderState();
  const { isDesktopAndAbove } = useViewport();
  const { showEligibleInsiderMessaging } = useShowEligibleInsiderMessaging();

  return (
    isDesktopAndAbove &&
    !insiderEuropeSearchBannerDismissed &&
    isInsiderPaused &&
    !showEligibleInsiderMessaging
  );
};

const getMembershipState = (
  isMembershipPaused: boolean,
  hasChurnedFromInsider: boolean
) => {
  if (isMembershipPaused) {
    return "PAUSED";
  }

  if (hasChurnedFromInsider) {
    return "CHURNED";
  }

  return "TRIAL_ELIGIBLE";
};
